import {
    IonContent,
    IonPage,
    IonSkeletonText,
    IonIcon,
    IonLabel,
    IonCard,
    IonItem,
    IonList,
    IonBadge,
    IonListHeader,
    IonThumbnail,
    IonImg,
    useIonAlert,
    IonHeader,
    IonToolbar,
    IonButton,
    IonInput,
    IonInfiniteScroll, 
    IonInfiniteScrollContent,
    useIonModal,
    useIonToast,
    IonRefresher,
    IonRefresherContent,
    RefresherEventDetail
} from '@ionic/react';
import React, { useState } from 'react';
import { storefrontOutline, searchOutline, arrowBackOutline, warningOutline, chevronDownCircleOutline, personCircleOutline, businessOutline } from 'ionicons/icons';
import { RestApi } from '../services/Api';
import Detail from '../components/customer/Detail';
import { loadList } from '../models/Data';
import { useTranslation } from 'react-i18next';
import { useConstructor } from '../services/Library';
import { useHistory } from 'react-router';
import './Customer.scss';

const Customer: React.FC = () => {
  const [presentToast] = useIonToast();
  const { t } = useTranslation(["general"]);
  const [alert] = useIonAlert();
  const [isInfiniteDisabled, setInfiniteDisabled] = useState(false);
  const [getIdCustomer, setGetIdCustomer] = useState<any>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [loaded, setLoaded] = useState<boolean>(false);
  const [listCustomer, setListCustomer] = useState<any>([]);
  const [dataCustomer, setDataCustomer] = useState<any>([]);
  const history = useHistory();
  
  const handleDismissDetail = (e: any) => {
    dismissDetail();
  };

  const [presentDetail, dismissDetail] = useIonModal(Detail, {
    idCustomer: getIdCustomer,
    history : history,
    onDismissDetail: handleDismissDetail,
  });

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ cssClass: "alert-button-confirm", text: "Ok" }],
    });
  };

  const getCustomerList = async (page: string, next: boolean) => {
    await RestApi(page, "get", {}, "application/json")
      .then((res: any) => {
        if (res.data.status) {
          setDataCustomer(res.data.data);
          if (next) {
            setListCustomer((prevState: any) => [
              ...prevState,
              ...res.data.data.data,
            ]);
          } else {
            setListCustomer(res.data.data.data);
          }
        } else {
         setListCustomer([]);
         setDataCustomer([]);
         presentToast({
           message: res.data.message,
           duration: 2500,
           color: "danger",
           icon: warningOutline,
         });       
        }
        setLoaded(true);
      })
      .catch((err: any) => {
        massageAlert('error', err.message);
        setLoaded(true);
      });
  };

  const getCustomerDetail = async (id: string) => {
    setGetIdCustomer(id);
    presentDetail({
      cssClass: "modal-fullscreen",
    });
  };
  
  const loadData = (ev: any) => {
    if (dataCustomer.current_page === dataCustomer.last_page) {
      setInfiniteDisabled(true);
      return;
    }
    getCustomerList(
      `customer?page=${dataCustomer.current_page + 1}&search=${searchText}`,
      true
    );
    ev.target.complete();
  };

  const onChangeSearch = (e: any) => {
    setSearchText(e.detail.value!);
    getCustomerList(`customer?page=1&search=${e.detail.value!}`, false);
  };

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getCustomerList(`customer?page=1&search=${searchText}`, false);
    event.detail.complete();
  };

  // Get icon based on customer title
  const getCustomerTitleIcon = (title: string) => {
    switch (title?.toUpperCase()) {
      case 'TOKO':
        return storefrontOutline;
      case 'PERORANGAN':
        return personCircleOutline;
      case 'PT':
      case 'CV':
      case 'OP':
        return businessOutline;
      default:
        return personCircleOutline;
    }
  };

  useConstructor(() => {
    getCustomerList(`customer?page=1&search=${searchText}`, false);
  });

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="toolbar-customer">
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            routerLink="/home"
            routerDirection="back"
            className=""
            size="small"
          >
            <IonIcon color="light" src={arrowBackOutline} />
          </IonButton>
          <div className="search">
            <IonItem lines="none" className="item-customer">
              <IonInput
                placeholder={t("search") + " " + t("module.customer")}
                className="input-customer"
                onIonInput={(e) => onChangeSearch(e)}
              ></IonInput>
              <IonIcon src={searchOutline} slot="start" />
              <IonBadge color="medium">{dataCustomer.total}</IonBadge>
            </IonItem>
          </div>
        </IonToolbar>
      </IonHeader>
      <IonContent className="customer-content">
        {!loaded && (
          <IonList>
            <IonListHeader>
              <IonSkeletonText
                animated={true}
                style={{ width: "80px" }}
              ></IonSkeletonText>
            </IonListHeader>
            {loadList.map((value: any, key: any) => (
              <IonItem key={key}>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonThumbnail>
                <IonLabel>
                  <h3>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "80%" }}
                    ></IonSkeletonText>
                  </h3>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "60%" }}
                    ></IonSkeletonText>
                  </p>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "30%" }}
                    ></IonSkeletonText>
                  </p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t("loading") ?? ""}
            refreshingSpinner="circles"
            refreshingText={t("loading") ?? ""}
          ></IonRefresherContent>
        </IonRefresher>
        {listCustomer.length && loaded ? (
          <IonList className="main-card">
            {listCustomer.map((value: any, key: any) => (
              <IonCard
                key={key}
                onClick={() => getCustomerDetail(value.customer_id)}
                style={{
                  borderRadius: 20,
                  boxShadow: "0 2px 12px 0 rgba(44,62,80,.09)",
                  border: "1px solid #eee",
                  transition: "box-shadow 0.2s",
                  cursor: "pointer",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 10px 0 10px",
                  }}
                >
                  <div
                    style={{
                      background: "#f0f5ff",
                      borderRadius: "50%",
                      width: 48,
                      height: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 18,
                      border: "1px solid #deebff",
                    }}
                  >
                    <IonIcon
                      style={{ fontSize: 28, color: "#861205" }}
                      icon={getCustomerTitleIcon(value.customer_title)}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "1.08em",
                        color: "#2d3436",
                        lineHeight: 1.2,
                        marginBottom: 2,
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {value.customer_name}
                    </div>
                    <div
                      style={{
                        color: "#861205",
                        fontSize: "0.98em",
                        marginBottom: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          padding: "2px 7px",
                          borderRadius: 5,
                          background: "#f7faff",
                          color: "#3867d6",
                          fontWeight: 500,
                          fontSize: "0.93em",
                        }}
                      >
                        {value.customer_type_name}
                      </span>
                      {value.customer_city && (
                        <span
                          style={{
                            fontSize: "0.95em",
                            color: "#505050",
                            background: "#f9f9fc",
                            borderRadius: 5,
                            padding: "2px 6px",
                          }}
                        >
                          {value.customer_city}
                        </span>
                      )}
                    </div>
                  </div>
                  <IonBadge
                    color="primary"
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      marginLeft: 15,
                      minWidth: 35,
                      borderRadius: 8,
                      background: "#e5f0fc",
                      color: "#2471a3",
                    }}
                  >
                    {value.customer_id}
                  </IonBadge>
                </div>
                <div
                  style={{
                    padding: "0 10px 0 86px",
                    marginBottom: 0,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      color: "#222",
                      fontWeight: 600,
                      fontSize: "1em",
                    }}
                  >
                    {value.customer_phone || "-"}
                  </div>
                  {value.customer_contact_name && (
                    <div
                      style={{
                        fontSize: "0.92em",
                        color: "#636e72",
                        fontWeight: 400,
                      }}
                    >
                      {value.customer_contact_name}
                      {value.customer_contact_title
                        ? ` (${value.customer_contact_title})`
                        : ""}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    padding: "0 10px 10px 86px",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        color: "#636e72",
                        fontSize: "0.96em",
                        marginBottom: 6,
                        lineHeight: 1.5,
                        wordBreak: "break-word",
                        whiteSpace: "pre-line",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        maxHeight: "3.2em",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                      title={
                        value.customer_address?.length
                          ? value.customer_address[0].customer_address
                          : "-"
                      }
                    >
                      {value.customer_address?.length
                        ? value.customer_address[0].customer_address
                        : "-"}
                    </div>
                    <div
                      style={{
                        color: "#b2bec3",
                        fontSize: "0.9em",
                        lineHeight: 1.2,
                        marginBottom: 2,
                        display:
                          value.subarea_name || value.area_name
                            ? undefined
                            : "none",
                      }}
                    >
                      {value.subarea_name && <span>{value.subarea_name}</span>}
                      {value.subarea_name && value.area_name && (
                        <span> - </span>
                      )}
                      {value.area_name && <span>{value.area_name}</span>}
                    </div>
                  </div>
                </div>
              </IonCard>
            ))}
          </IonList>
        ) : (
          <IonImg className="img-empty" src={"assets/img/search-empty.png"} />
        )}
        <IonInfiniteScroll
          onIonInfinite={loadData}
          threshold="100px"
          disabled={isInfiniteDisabled}
        >
          {dataCustomer.next_page_url && (
            <IonInfiniteScrollContent
              loadingSpinner="bubbles"
              loadingText="Loading more data..."
            >
              <IonItem>
                <IonThumbnail slot="start">
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonThumbnail>
                <IonLabel>
                  <h3>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "80%" }}
                    ></IonSkeletonText>
                  </h3>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "60%" }}
                    ></IonSkeletonText>
                  </p>
                  <p>
                    <IonSkeletonText
                      animated={true}
                      style={{ width: "30%" }}
                    ></IonSkeletonText>
                  </p>
                </IonLabel>
              </IonItem>
            </IonInfiniteScrollContent>
          )}
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default Customer;
